/**
 * SecretsPage Component
 *
 * Main page for managing secrets with search functionality.
 *
 * SECURITY NOTES:
 * - Secret values are NEVER displayed in the UI
 * - Values are write-only from the frontend
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { SecretTable } from '../components/SecretTable';
import { CreateSecretModal } from '../components/CreateSecretModal';
import { UpdateSecretModal } from '../components/UpdateSecretModal';
import { SecretDetailsModal } from '../components/SecretDetailsModal';
import { DeleteSecretModal } from '../components/DeleteSecretModal';
import { useSecrets } from '../hooks/useSecrets';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { SecretSanitized } from '../../../types/secret.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * SecretsPage component
 */
export const SecretsPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedSecret, setSelectedSecret] = useState<SecretSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'resources:secret:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'resources:secret:update');
  const canDelete = hasPermission(activeRole?.permissions, 'resources:secret:delete');

  // Debounced search (300ms delay)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch secrets
  const {
    data: secrets = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useSecrets(tenantId, {
    search: debouncedSearch || undefined,
  });

  // Filter secrets locally (additional filtering if needed)
  const filteredSecrets = useMemo(() => {
    if (!secrets) return [];

    let filtered = [...secrets];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.secretName.toLowerCase().includes(query) ||
          item.secretCode.toLowerCase().includes(query) ||
          (item.description && item.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [secrets, debouncedSearch]);

  const handleViewDetails = (secret: SecretSanitized) => {
    setSelectedSecret(secret);
    setShowDetailsModal(true);
  };

  const handleEdit = (secret: SecretSanitized) => {
    setSelectedSecret(secret);
    setShowUpdateModal(true);
  };

  const handleDelete = (secret: SecretSanitized) => {
    setSelectedSecret(secret);
    setShowDeleteModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Secrets</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage encrypted secrets and application access
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>
              Create Secret
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search secrets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Secrets Table */}
        <SecretTable
          secrets={filteredSecrets}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canUpdate}
          canDelete={canDelete}
        />

        {/* Modals */}
        <CreateSecretModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedSecret && (
          <SecretDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedSecret(null);
            }}
            tenantId={tenantId}
            secretId={selectedSecret.secretId}
          />
        )}

        {showUpdateModal && selectedSecret && (
          <UpdateSecretModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedSecret(null);
            }}
            tenantId={tenantId}
            secret={selectedSecret}
          />
        )}

        {showDeleteModal && selectedSecret && (
          <DeleteSecretModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedSecret(null);
            }}
            tenantId={tenantId}
            secret={selectedSecret}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
